import { createAdminApi, type AdminApi } from "../../lib/api";

// 住所 → 緯度経度 (順ジオコーディング)。
// ブラウザから外部 API を直叩きせず、自前 API (oRPC geocoding.forward) 経由でプロキシする。
// API 側は Yahoo!ジオコーダ (地番・戸番レベル) を優先し、デジタル庁 abr-geocoder に
// フォールバックする (Issue #612)。ヒットなし・通信失敗時は null を返し、
// 呼出側は地図手動補正にフォールバックする。
//
// geocodeJpAddress はイベントハンドラ等の React 外からも呼ばれるため、useAdminApi (Context)
// ではなくモジュールスコープの遅延シングルトン client を使い、シグネチャを維持する。
let apiSingleton: AdminApi | null = null;
function getApi(): AdminApi {
  if (!apiSingleton) apiSingleton = createAdminApi();
  return apiSingleton;
}

export async function geocodeJpAddress(
  address: string,
): Promise<{ lat: number; lng: number } | null> {
  try {
    const { data } = await getApi().geocoding.forward({ address });
    return data;
  } catch {
    return null;
  }
}

/** 番地・号未満の低精度ヒット (candidate) も座標込みで返す。要確認 UI を持つ呼出側専用。 */
export interface GeocodeCandidate {
  lat: number;
  lng: number;
  matchLevel: string | null;
  /** true: 番地・号未満の低精度ヒットで要確認、false: 番地・号レベルで確定済み */
  needsConfirmation: boolean;
}

// geocodeJpAddress と同じ API を叩くが、低精度ヒット (candidate) を握りつぶさず座標を返す。
// 呼出側で「要確認」バッジ・地図ピン再確認を促す UI を出すために使う。
export async function geocodeJpAddressWithConfidence(
  address: string,
): Promise<GeocodeCandidate | null> {
  try {
    const { data, candidate } = await getApi().geocoding.forward({ address });
    if (data)
      return {
        lat: data.lat,
        lng: data.lng,
        matchLevel: data.match_level,
        needsConfirmation: false,
      };
    if (candidate)
      return {
        lat: candidate.lat,
        lng: candidate.lng,
        matchLevel: candidate.match_level,
        needsConfirmation: true,
      };
    return null;
  } catch {
    return null;
  }
}
